import React, { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import apiService from '../services/apiService';
import { PERIOD_OPTIONS } from './PeriodSelector';
import './DailyDataChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const METRIC_OPTIONS = [
  { id: 'temperature', label: 'Temperature', unit: '°C', color: { max: 'rgb(239, 68, 68)', avg: 'rgb(6, 182, 212)', min: 'rgb(34, 197, 94)' } },
  { id: 'humidity', label: 'Humidity', unit: '%', color: { max: 'rgb(236, 72, 153)', avg: 'rgb(6, 182, 212)', min: 'rgb(34, 197, 94)' } },
  { id: 'pressure', label: 'Pressure', unit: ' hPa', color: { max: 'rgb(249, 115, 22)', avg: 'rgb(6, 182, 212)', min: 'rgb(34, 197, 94)' }, scale: { min: 930, max: 1000 } },
  { id: 'wind_speed', label: 'Wind Speed', unit: ' kts', color: { max: 'rgb(236, 72, 153)', avg: 'rgb(6, 182, 212)', min: 'rgb(34, 197, 94)' }, convert: (v) => v * 1.94384 },
  { id: 'solar_radiation', label: 'Solar Radiation', unit: ' W/m²', color: { max: 'rgb(251, 191, 36)', avg: 'rgb(6, 182, 212)', min: 'rgb(217, 119, 6)' } },
  { id: 'dewpoint', label: 'Dewpoint', unit: '°C', color: { max: 'rgb(236, 72, 153)', avg: 'rgb(6, 182, 212)', min: 'rgb(34, 197, 94)' } }
];

const DailyDataChart = ({ selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month }) => {
  const [rawData, setRawData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('temperature');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [dataQuality, setDataQuality] = useState(null);

  const loadData = useCallback(async (period, year, month, year12, month12) => {
    try {
      setLoading(true);
      setError(null);
      
      const periodConfig = PERIOD_OPTIONS.find(p => p.id === period);
      let response;
      if (period === 'select') {
        response = await apiService.getDailySummary(null, year, month);
      } else if (period === '12months') {
        response = await apiService.getDailySummary(12, year12, month12);
      } else {
        response = await apiService.getDailySummary(periodConfig.months);
      }
      
      if (!response.data || response.data.length === 0) {
        setError('No data available for this period');
        setRawData(null);
        setChartData(null);
        setDataQuality(null);
        return;
      }

      setDateRange({
        start: response.start_date,
        end: response.end_date
      });

      setDataQuality(response.dataQuality);
      setRawData(response.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  const buildChartData = useCallback(() => {
    if (!rawData) return;

    const metricConfig = METRIC_OPTIONS.find(m => m.id === selectedMetric);
    const dataPoints = rawData.length;

    const labels = rawData.map(d => {
      const date = new Date(d.date);
      if (dataPoints > 365) {
        return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      } else if (dataPoints > 60) {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    });

    // Helper to get value - return null for missing data (shows gap in chart)
    // Apply unit conversion if defined (e.g., m/s to knots for wind speed)
    const getValue = (val) => {
      if (val === null || val === undefined) return null;
      const num = parseFloat(val);
      if (isNaN(num)) return null;
      return metricConfig.convert ? metricConfig.convert(num) : num;
    };

    const data = {
      labels,
      datasets: [
        {
          label: `Max ${metricConfig.label}`,
          data: rawData.map(d => getValue(d[selectedMetric]?.max)),
          borderColor: metricConfig.color.max,
          backgroundColor: metricConfig.color.max.replace('rgb', 'rgba').replace(')', ', 0.1)'),
          borderWidth: 2,
          pointRadius: dataPoints > 100 ? 0 : 1,
          pointHoverRadius: 4,
          tension: 0.3,
          fill: false,
          spanGaps: false
        },
        {
          label: `Avg ${metricConfig.label}`,
          data: rawData.map(d => getValue(d[selectedMetric]?.avg)),
          borderColor: metricConfig.color.avg,
          backgroundColor: metricConfig.color.avg.replace('rgb', 'rgba').replace(')', ', 0.1)'),
          borderWidth: 2,
          pointRadius: dataPoints > 100 ? 0 : 1,
          pointHoverRadius: 4,
          tension: 0.3,
          fill: false,
          spanGaps: false
        },
        {
          label: `Min ${metricConfig.label}`,
          data: rawData.map(d => getValue(d[selectedMetric]?.min)),
          borderColor: metricConfig.color.min,
          backgroundColor: metricConfig.color.min.replace('rgb', 'rgba').replace(')', ', 0.1)'),
          borderWidth: 2,
          pointRadius: dataPoints > 100 ? 0 : 1,
          pointHoverRadius: 4,
          tension: 0.3,
          fill: false,
          spanGaps: false
        }
      ]
    };

    setChartData(data);
  }, [rawData, selectedMetric]);

  useEffect(() => {
    loadData(selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month);
  }, [selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month, loadData]);

  useEffect(() => {
    buildChartData();
  }, [rawData, selectedMetric, buildChartData]);

  const handleMetricChange = (metricId) => {
    setSelectedMetric(metricId);
  };

  const getOptions = () => {
    const dataPoints = chartData?.labels?.length || 0;
    const metricConfig = METRIC_OPTIONS.find(m => m.id === selectedMetric);
    
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: 'white',
            font: {
              size: 12
            },
            usePointStyle: true,
            pointStyle: 'circle'
          }
        },
        title: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            label: function(context) {
              return `${context.dataset.label}: ${context.parsed.y}${metricConfig.unit}`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            color: 'rgba(255, 255, 255, 0.1)',
            drawBorder: false
          },
          ticks: {
            color: 'rgba(255, 255, 255, 0.7)',
            maxRotation: 45,
            minRotation: 45,
            font: {
              size: 10
            },
            maxTicksLimit: dataPoints > 365 ? 12 : (dataPoints > 60 ? 15 : 20)
          }
        },
        y: {
          min: metricConfig.scale?.min,
          max: metricConfig.scale?.max,
          grid: {
            color: 'rgba(255, 255, 255, 0.1)',
            drawBorder: false
          },
          ticks: {
            color: 'rgba(255, 255, 255, 0.7)',
            callback: function(value) {
              return value + metricConfig.unit;
            }
          },
          title: {
            display: true,
            text: `${metricConfig.label} (${metricConfig.unit.trim()})`,
            color: 'rgba(255, 255, 255, 0.7)'
          }
        }
      }
    };
  };

  const formatDateRange = () => {
    if (!dateRange.start || !dateRange.end) return '';
    const start = new Date(dateRange.start).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric' 
    });
    const end = new Date(dateRange.end).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric' 
    });
    return `${start} - ${end}`;
  };

  return (
    <div className="daily-data-chart">
      <div className="chart-header">
        <h3>Daily Data</h3>
        <div className="metric-tabs">
          {METRIC_OPTIONS.map(option => (
            <button
              key={option.id}
              className={`metric-tab ${selectedMetric === option.id ? 'active' : ''}`}
              onClick={() => handleMetricChange(option.id)}
              disabled={loading}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      
      {dateRange.start && (
        <div className="date-range">
          {formatDateRange()} ({chartData?.labels?.length || 0} days)
          {dataQuality && (
            <span className="data-quality">
              {' '} | Data: {dataQuality.daysWithData}/{dataQuality.totalDays} days ({dataQuality.percentComplete}%)
              {dataQuality.daysWithoutData > 0 && (
                <span className="missing-days"> - {dataQuality.daysWithoutData} days missing</span>
              )}
            </span>
          )}
        </div>
      )}

      <div className="chart-container">
        {loading ? (
          <div className="chart-loading">Loading data...</div>
        ) : error ? (
          <div className="chart-error">{error}</div>
        ) : (
          chartData && <Line options={getOptions()} data={chartData} />
        )}
      </div>
    </div>
  );
};

export default DailyDataChart;
